'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useReactiveVar } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { FileWithPath } from '@mantine/dropzone';
import { Trans, useTranslation } from 'react-i18next';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// Store
import useAccount from '@features/Account/store/useAccount';

// Helpers & utils
import { formatSize, getMaxUploadSize } from '@helpers/size';
import invokePromptConfirm from '@features/Prompt/PromptConfirm';

// API & Types
import {
  uploadingFilesVar,
  failedUploadsVar,
  registerUploadAbortController,
  cleanupUploadAbortController,
} from '@features/Resource/store/uploadingFiles';
import {
  Resource,
  User,
  ResourceType,
  Workspace,
  useCreateResourcesMutation,
  useGetUploadSignedUrlLazyQuery,
  Permission,
  ResourceInput,
  useInitializeMultipartUploadMutation,
  useGetMultipartPreSignedUrlsLazyQuery,
  useFinalizeMultipartUploadMutation,
  ActivityStatus,
} from '@app-types/blackroom-api';
import {
  ResourceScope,
  ResourceUploadStatus,
  TrackedResource,
  UploadStub,
  UploadStubFile,
  UploadedStub,
} from '@app-types/blackroom-app';
import {
  ignoredExtensions,
  ResourceMimeType,
} from '@app-types/blackroom-app/resourceMimeType';
import { UseUploaderRet, ValidateAndUploadFilesInput } from './types';
import { List, Stack, Text } from '@mantine/core';
import { UploadFilesData } from '@app/features/Resource/DialogResourceUpload/types';
import { getEntryFile } from '@app/shared/ui-kit/molecules/UploadArea/utils/getEntryFile';
import PromiseValue from '@ui-kit/atoms/PromiseValue';
import { getFileExtension } from '@app/shared/helpers/file';
import { validateUploadFileAsync } from '@app/shared/ui-kit/molecules/UploadArea/utils/validateUploadFile';
import NEW_RESOURCE_FRAGMENT from '@app/features/Resource/api/newResourceFragment';

/* -------------------------------------------------------------------------- */
/*                                  Utilities                                 */
/* -------------------------------------------------------------------------- */

/**
 * Type guard to check if an error is an AbortError from fetch cancellation
 */
const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

const isUploadStubFile = (stub: UploadStub): stub is UploadStubFile =>
  !!stub.file;

/* -------------------------------------------------------------------------- */
/*                               Implementation                               */
/* -------------------------------------------------------------------------- */

function useUploader(scope: ResourceScope = 'my'): UseUploaderRet {
  /* ********************************** HOOKS ********************************* */

  // Context
  const workspace = useWorkspace();

  // Store
  const uploadingFiles = useReactiveVar(uploadingFilesVar);

  // API
  const [getUploadSignedUrlQuery] = useGetUploadSignedUrlLazyQuery();
  const [initializeMultipartUpload] = useInitializeMultipartUploadMutation();
  const [finalizeMultipartUpload] = useFinalizeMultipartUploadMutation();
  const [getMultipartPreSignedUrls] = useGetMultipartPreSignedUrlsLazyQuery();
  const [createResources] = useCreateResourcesMutation();

  // Auth
  const { account: user } = useAccount();

  // Localization
  const { t } = useTranslation(['Common', 'Resource']);

  /* **************************** INTERNAL METHODS **************************** */

  /**
   * Generates a resource stub used to mimic a resource  generally in the context
   * of Optimistic UI rendering
   * @param {User} me Allows to inject user account from a component
   * @param {Partial<Resource>} overridingPayload Allow to to override the
   * default resource with pre-computed values
   * @returns {Resource}
   */
  const _generateResourceStub = (
    overridingPayload?: Partial<Resource>,
  ): Resource => ({
    __typename: 'Resource',
    canDownload: true,
    id: uuidv4(),
    publicId: '',
    workspaceId: workspace.id,
    name: 'Unnamed file',
    type: ResourceType.File,
    mimeType: ResourceMimeType.pdf,
    parentId: null,
    index: '',
    path: [],
    size: 0,
    pspdfKitId: '',
    redactions: [],
    entitiesSearchMatches: [],
    ignoredRedactionSearch: [],
    isFavorite: false,
    isFaulty: false,
    isQaOnly: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    archivedAt: null,
    createdBy: (user ?? {
      id: 'temp-id',
      firstName: '',
      lastName: '',
      email: '',
      pictureURL: '',
      activityStatus: ActivityStatus.Online,
    }) as User,
    userPermissions: [Permission.ResourceAccessOriginal],
    resourceAccess: [],
    activities: [],
    shouldRestoreAsArchived: false,
    accesses: [],
    descendantsAccesses: [],
    inheritedAccesses: [],
    requestFiles: [],
    ...(overridingPayload ?? {}),
  });

  /**
   * Merge multiple resource upload statuses into a single one
   * Used to determine the overall upload status of a folder
   * Order of priority is: Pending > InProgress > Failed > Done
   */
  const _mergeResourceUploadStatus = (
    ...status: ResourceUploadStatus[]
  ): ResourceUploadStatus => {
    const statusOrder = [
      ResourceUploadStatus.Pending,
      ResourceUploadStatus.InProgress,
      ResourceUploadStatus.Failed,
      ResourceUploadStatus.Done,
    ];
    let result: number = statusOrder.length - 1;

    status.forEach((value) => {
      result = Math.min(result, statusOrder.indexOf(value));
    });

    return statusOrder[result];
  };

  const _resetUploadingFilesIfDone = () => {
    const allUploadingFiles = Object.values(uploadingFiles).flatMap(
      ({ files }) => [...files],
    );
    const previousUploadingsDone = allUploadingFiles.every(
      (uploadingFile) =>
        uploadingFile.uploadStatus === ResourceUploadStatus.Done,
    );
    if (previousUploadingsDone) {
      // we delete keys because we have to continue using the same object
      Object.keys(uploadingFiles).forEach((key) => delete uploadingFiles[key]);
      uploadingFilesVar(uploadingFiles);
    }
  };

  /**
   * @returns the tracked files and folders from a list of upload stubs. Calculate the status of folders based on their children.
   */
  const _getTrackedResources = (
    stubs: UploadStub[],
    { initialize = false } = {},
  ): TrackedResource[] => {
    const result = new Array<TrackedResource>();

    stubs.forEach((stub) => {
      const uploadPathElements = stub.uploadPath.split('/');
      const rootFolderName =
        uploadPathElements.length > 1 ? uploadPathElements[0] : undefined;

      if (rootFolderName) {
        const rootFolderStub = result.find(
          ({ type, name }) =>
            type === ResourceType.Folder && name === rootFolderName,
        );

        if (rootFolderStub) {
          if (initialize) {
            rootFolderStub.size += stub.size ?? 0;
          } else {
            rootFolderStub.uploadStatus = _mergeResourceUploadStatus(
              rootFolderStub.uploadStatus,
              stub.uploadStatus,
            );
            rootFolderStub.size += stub.size ?? 0;
          }
        } else {
          // Find the actual folder stub to get its uploadId for proper cancellation tracking
          const existingFolderStub = stubs.find(
            (s) =>
              s.type === ResourceType.Folder && s.uploadPath === rootFolderName,
          );

          const newFolderStub = {
            ..._generateResourceStub({
              name: rootFolderName,
              type: ResourceType.Folder,
              size: 0,
            }),
            uploadId: existingFolderStub?.uploadId ?? uuidv4(),
            uploadStatus: initialize
              ? ResourceUploadStatus.InProgress
              : ResourceUploadStatus.Done,
            uploadPath: rootFolderName,
          };
          result.push(newFolderStub);
        }
      } else {
        result.push(stub);
        if (!initialize && !stub.file) {
          stub.uploadStatus = ResourceUploadStatus.Done;
        }
      }
    });

    return result;
  };

  /**
   * Uploads a resource to S3 and returns its path
   * @param namePath Concatenated resource path delimited with "/"
   * relatively to parent folder, workspace and tenant
   * @returns Resource path on S3 bucket
   */
  const _uploadObject = async (
    initialResourceId: string,
    fileKeyPrefix: string,
    fileBody: FileWithPath,
    abortSignal?: AbortSignal,
  ): Promise<{ id: string; storagePath: string }> => {
    const chunkSize = 1024 * 1024 * 5; // 5MB
    const MAX_RETRIES = 2; // Maximum number of retries (3 attempts total)
    const RETRY_DELAY = 1000; // Delay between retries in ms

    if (fileBody.size === 0) {
      throw new Error('File is empty');
    }

    // For large files, we use multipart upload
    if (fileBody.size > chunkSize) {
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const id = attempt === 0 ? initialResourceId : uuidv4();
        const fileKey = `${fileKeyPrefix}/${id}/original`;

        try {
          const { data: initializeMultipartUploadData } =
            await initializeMultipartUpload({
              variables: { fileKey },
            });

          if (!initializeMultipartUploadData) {
            throw new Error('Error during initialization of multipart upload');
          }

          const numberOfparts = Math.ceil(fileBody.size / chunkSize);

          const { data: getMultipartPreSignedUrlsData } =
            await getMultipartPreSignedUrls({
              variables: {
                fileKey:
                  initializeMultipartUploadData.initializeMultipartUpload
                    .fileKey,
                uploadId:
                  initializeMultipartUploadData.initializeMultipartUpload
                    .uploadId,
                parts: numberOfparts,
              },
            });

          if (!getMultipartPreSignedUrlsData) {
            throw new Error('Error during generation of presigned urls');
          }

          // Upload each part with retry logic
          const uploadResults = await Promise.all(
            getMultipartPreSignedUrlsData.getMultipartPreSignedUrls.map(
              async (preSignedUrl, index) => {
                const start = index * chunkSize;
                const end = start + chunkSize;

                // Retry logic for individual part upload
                for (
                  let partAttempt = 0;
                  partAttempt <= MAX_RETRIES;
                  partAttempt++
                ) {
                  try {
                    const uploadResult = await fetch(preSignedUrl, {
                      method: 'PUT',
                      body: fileBody.slice(start, end),
                      signal: abortSignal,
                    });

                    if (!uploadResult.ok) {
                      throw new Error(
                        `Part upload failed with status: ${uploadResult.status}`,
                      );
                    }

                    return uploadResult;
                  } catch (error) {
                    // If aborted, don't retry - throw immediately
                    if (isAbortError(error)) {
                      throw error;
                    }
                    if (partAttempt === MAX_RETRIES) {
                      throw new Error(
                        `Failed to upload part ${index + 1} after ${MAX_RETRIES + 1} attempts`,
                      );
                    }
                    console.error(
                      `Part ${index + 1} upload attempt ${partAttempt + 1} failed:`,
                      error,
                    );
                    await new Promise((resolve) =>
                      setTimeout(resolve, RETRY_DELAY),
                    );
                  }
                }
                throw new Error(`Failed to upload part ${index + 1}`); // Shouldn't reach here
              },
            ),
          );

          const { data: finalizeMultipartUploadData } =
            await finalizeMultipartUpload({
              variables: {
                finalizeMultipartUploadInput: {
                  fileKey:
                    initializeMultipartUploadData.initializeMultipartUpload
                      .fileKey,
                  uploadId:
                    initializeMultipartUploadData.initializeMultipartUpload
                      .uploadId,
                  parts: uploadResults.map((uploadResult, index) => ({
                    ETag: (uploadResult.headers.get('ETag') || '').replaceAll(
                      '"',
                      '',
                    ),
                    PartNumber: index + 1,
                  })),
                },
              },
            });

          if (!finalizeMultipartUploadData?.finalizeMultipartUpload) {
            throw new Error('Error during finalization of multipart upload');
          }

          const pathUrlObject = new URL(
            finalizeMultipartUploadData.finalizeMultipartUpload,
          );
          const { origin } = pathUrlObject;

          const storagePath = `${origin}/${fileKey}`;
          return { id, storagePath };
        } catch (error) {
          // If aborted, don't retry - throw immediately
          if (isAbortError(error)) {
            throw error;
          }
          lastError = error as Error;
          console.error(
            `Multipart upload attempt ${attempt + 1} failed:`,
            error,
          );

          if (attempt < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
            continue;
          }
        }
      }

      // If we get here, all attempts failed
      console.error('All multipart upload attempts failed');
      throw lastError || new Error('Could not upload file after all retries');
    }

    // For small files, implement retry logic
    let lastError: Error | null = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const id = attempt === 0 ? initialResourceId : uuidv4();
      const fileKey = `${fileKeyPrefix}/${id}/original`;

      try {
        // Get upload URL
        const { data } = await getUploadSignedUrlQuery({
          variables: { fileKey },
        });

        if (!data?.resourceUploadSignedUrl) {
          throw new Error('Could not get upload URL');
        }

        // Attempt the upload
        const bucketResponse = await fetch(data.resourceUploadSignedUrl, {
          method: 'PUT',
          headers: { 'If-None-Match': '*' },
          body: fileBody,
          signal: abortSignal,
        });

        if (!bucketResponse.ok) {
          throw new Error(
            `Upload failed with status: ${bucketResponse.status}`,
          );
        }

        const pathUrlObject = new URL(bucketResponse.url);
        const { origin, pathname } = pathUrlObject;
        const storagePath = `${origin}${pathname}`;

        return { id, storagePath };
      } catch (error) {
        // If aborted, don't retry - throw immediately
        if (isAbortError(error)) {
          throw error;
        }
        lastError = error as Error;
        console.error(`Upload attempt ${attempt + 1} failed:`, error);

        // If this was not our last attempt, wait before retrying
        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          continue;
        }
      }
    }

    // If we get here, all attempts failed
    console.error('All upload attempts failed');
    throw lastError || new Error('Could not upload file after all retries');
  };

  /**
   * Uploads a file to S3 and places it in the target folder object
   */
  const _uploadResource = async (
    stub: UploadStubFile,
    forWorkspace: Pick<Workspace, 'id'> & {
      tenant: { id: string };
    } = workspace,
  ): Promise<ResourceInput> => {
    // Create and register AbortController for this upload
    const abortController = new AbortController();
    registerUploadAbortController(stub.uploadId, abortController);

    try {
      const { id, file, name, mimeType } = stub;

      // Preliminary resource payload. Used to build optimistic UI stub
      // based on partially provided input
      const filePayload: ResourceInput = {
        id,
        name,
        type: ResourceType.File,
        mimeType,
        size: file.size,
      };

      // Folder path prefixed with tenant and workspace paths
      const resourceKeyPrefix = `${encodeURIComponent(forWorkspace.tenant.id)}/${encodeURIComponent(
        forWorkspace.id,
      )}/resources`;

      try {
        // Upload file to S3
        const uploadedObject = await _uploadObject(
          id,
          resourceKeyPrefix,
          file,
          abortController.signal,
        );

        // Clean up AbortController on success
        cleanupUploadAbortController(stub.uploadId);

        // Succesful return point
        return { ...filePayload, ...uploadedObject };
      } catch (error) {
        // Clean up AbortController on failure
        cleanupUploadAbortController(stub.uploadId);

        // Don't add to failed uploads if it was cancelled
        if (isAbortError(error)) {
          throw error;
        }

        // Add to failed uploads
        const failedUploads = failedUploadsVar();
        failedUploadsVar([
          ...failedUploads,
          {
            fileName: name,
            error: (error as Error)?.message || 'Unknown error during upload',
            attempts: 3, // We know we tried 3 times in _uploadObject
          },
        ]);
        throw error;
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  /**
   * Creates resources on the Blackroom API
   */
  const _createResourcesOnServer = async ({
    resources,
    workspaceId,
    parentId,
  }: {
    resources: ResourceInput[];
    workspaceId: string;
    parentId: string | null;
  }) => {
    return createResources({
      variables: { createResourcesInput: { resources, workspaceId, parentId } },
      update: (cache, { data }) => {
        cache.modify({
          fields: {
            resources: (existingResourcesQueryResult = { nodes: [] }) => {
              const newResourcesRefs =
                data?.createResources.map((cr) =>
                  cache.writeFragment({
                    data: { ...cr, workspaceId: workspaceId },
                    fragment: NEW_RESOURCE_FRAGMENT,
                  }),
                ) ?? [];

              return {
                ...existingResourcesQueryResult,
                nodes: [
                  ...newResourcesRefs,
                  ...existingResourcesQueryResult.nodes,
                ],
              };
            },
          },
        });
      },
    });
  };

  /**
   * Create all stubs from a list of Files or FileSystemEntries.
   * @return all elements, whether they are files or folders.
   * If we receive an array of Files (from "input"), we can't have folders in the result because folders are dismissed.
   * If we receive an array of FileSystemEntries (from "drag and drop"), we can have folders in the result.
   */
  const _getAllUploadStubs = async (
    entries: (File | FileSystemEntry)[],
    entriesUploadPath = Array<string>(),
    parentUploadId?: string,
  ): Promise<(UploadStub | UploadStubFile)[]> => {
    const result = Array<UploadStub>();

    await Promise.all(
      entries.map(async (entry) => {
        const file = await getEntryFile(entry);

        let isAcceptedFile = true;
        let mimeType = file?.type?.toLowerCase(); // Normalize to lowercase for consistency
        if (file) {
          const validationResult = await validateUploadFileAsync(file);
          const { validation, extension, detectedMimeType } = validationResult;
          if (!extension) {
            isAcceptedFile = validation;
          } else {
            isAcceptedFile =
              validation && !ignoredExtensions.includes(extension);
          }
          // Use detected MIME type from file-type library if available
          if (detectedMimeType) {
            mimeType = detectedMimeType;
          } else if (!file.type && extension) {
            mimeType = (ResourceMimeType as any)[extension];
          }
        }

        if (file && isAcceptedFile) {
          const uploadPath =
            entry instanceof File
              ? entry.webkitRelativePath
              : [...entriesUploadPath, entry.name].join('/');
          result.push({
            ..._generateResourceStub({
              name: file.name,
              mimeType,
              size: file.size,
            }),
            file,
            uploadId: uuidv4(),
            uploadStatus: ResourceUploadStatus.InProgress,
            uploadPath,
            parentUploadId,
          });
        } else if (isAcceptedFile) {
          // we get a folder
          const folderEntry = entry as FileSystemDirectoryEntry;
          const children = await new Promise<FileSystemEntry[]>(
            (resolve, reject) => {
              folderEntry.createReader().readEntries(resolve, reject);
            },
          );

          const folderUploadId = uuidv4();

          result.push({
            ..._generateResourceStub({
              id: uuidv4(),
              name: folderEntry.name,
              type: ResourceType.Folder,
              storagePath: '',
            }),
            uploadId: folderUploadId,
            uploadStatus: ResourceUploadStatus.Done,
            uploadPath: [...entriesUploadPath, entry.name].join('/'),
            parentUploadId,
          });

          const childrenStubs = await _getAllUploadStubs(
            children,
            [...entriesUploadPath, folderEntry.name],
            folderUploadId,
          );
          result.push(...childrenStubs);
        }
      }),
    );

    return result;
  };

  /* **************************** EXPORTED METHODS **************************** */

  /**
   * Creates a folder on S3 and links it to Blackroom
   * @param {String} name Name to use to create the folder
   * @param {String} parentId Resource id of the parent folder. If null, root folder.
   */
  const createFolder = async (name: string, parentId: string | null = null) => {
    _resetUploadingFilesIfDone();

    // Preliminary resource payload. Used to build optimistic UI stub
    // based on partially provided input
    const suppliedId = uuidv4();
    const folderPayload: ResourceInput = {
      id: suppliedId,
      name,
      type: ResourceType.Folder,
      storagePath: '',
      directoryPath: '',
      mimeType: null,
      size: 0,
    };
    const workspaceId = workspace.id;

    // Create folder on Blackroom
    const createdFolder = await _createResourcesOnServer({
      workspaceId,
      parentId,
      resources: [folderPayload],
    });

    if (createdFolder?.data?.createResources) {
      // Optional return point for UI management
      return createdFolder?.data?.createResources[0] as Resource;
    }

    throw new Error('could not create resource');
  };

  /**
   * Processes files selected by used (picked from input or drag and drop)
   * to determine their attributes and trigger upload job. Will also
   * sort them by folder if it's a folder upload
   * @param files HTML input / Dropzone files
   */
  const uploadSelectedResources = async ({
    files,
    parent,
    overrideWithWorkspace,
    onReady,
    createResourcesFn,
  }: {
    files: UploadFilesData;
    parent?: Resource;
    overrideWithWorkspace?: Pick<Workspace, 'id'> & { tenant: { id: string } };
    onReady?: () => void;
    createResourcesFn?: (input: {
      resources: ResourceInput[];
      workspaceId: string;
      parentId: string | null;
    }) => Promise<any>;
  }): Promise<void> => {
    _resetUploadingFilesIfDone();

    const uploadBatchId = window.crypto.randomUUID();
    const workspaceId = overrideWithWorkspace
      ? overrideWithWorkspace.id
      : workspace.id;
    const parentId = parent?.id ?? null;

    try {
      // Reset failed uploads for this batch
      failedUploadsVar([]);

      /* ****************************** QUEUE UPLOADS ***************************** */

      /**
       * Flat list of all files to upload, including nested ones (not folders)
       */
      const allUploadStubs = await _getAllUploadStubs(files);

      /**
       * Flattened list of all top-level resources (files and folders)
       */
      const allTrackedResources = _getTrackedResources(allUploadStubs, {
        initialize: true,
      });

      if (!overrideWithWorkspace) {
        uploadingFilesVar({
          ...uploadingFiles,
          [uploadBatchId]: {
            files: allUploadStubs,
            topLevelResources: allTrackedResources,
            isCreateResourcesDone: false,
            parentId: parentId || undefined,
          },
        });
      }

      onReady?.();

      // Upload files to S3
      // (this will also upload files processable by PSPDFKit to PSPDFKit Server, server-to-server)
      const maxConcurrentUploads = 1000; // To prevent "resource error" from the browser
      const uploadStubsInBatches = async (stubs: UploadStub[]) => {
        const uploadedStubs: UploadedStub[] = [];

        for (let i = 0; i < stubs.length; i += maxConcurrentUploads) {
          const batch = stubs.slice(i, i + maxConcurrentUploads);
          const uploadPromises: Promise<UploadedStub>[] = batch.map(
            async (stub, index) => {
              if (!isUploadStubFile(stub)) {
                // if we have a folder, we don't upload it to S3
                return stub;
              }

              let uploadedStub: UploadedStub;
              let uploadStatus: ResourceUploadStatus;
              let uploadDuration: number | undefined;
              const uploadStartedAt = Date.now();

              try {
                const uploadedResource = await _uploadResource(
                  stub,
                  overrideWithWorkspace ?? workspace,
                );

                uploadedStub = {
                  ...stub,
                  id: uploadedResource.id,
                  resource: uploadedResource,
                };
                uploadStatus = ResourceUploadStatus.Done;
                uploadDuration = Date.now() - uploadStartedAt;
              } catch (error) {
                // If cancelled, skip tracking update entirely
                if (isAbortError(error)) {
                  // Mark as cancelled in the local array so other code doesn't break
                  allUploadStubs[i + index].uploadStatus =
                    ResourceUploadStatus.Failed;
                  return stub;
                }
                // Could not upload file to S3, mark as failed
                uploadedStub = stub;
                uploadStatus = ResourceUploadStatus.Failed;
              }

              // Complete activity tracking - use fresh state, not stale closure
              const currentUploadingFiles = uploadingFilesVar();
              const currentBatch = currentUploadingFiles?.[uploadBatchId];

              // Only update if this batch and this specific file still exist (not cancelled)
              if (!currentBatch) {
                return uploadedStub;
              }

              const fileStillExists = currentBatch.files?.some(
                (f) => f.uploadId === stub.uploadId,
              );

              if (!fileStillExists) {
                return uploadedStub;
              }

              // Update only the files that still exist in the current state
              const updatedFiles = currentBatch.files.map((f) =>
                f.uploadId === stub.uploadId
                  ? { ...f, id: uploadedStub.id, uploadStatus, uploadDuration }
                  : f,
              );

              uploadingFilesVar({
                ...currentUploadingFiles,
                [uploadBatchId]: {
                  ...currentBatch,
                  files: updatedFiles,
                  topLevelResources: _getTrackedResources(updatedFiles),
                },
              });

              return uploadedStub;
            },
          );

          const batchResults = await Promise.all(uploadPromises);
          uploadedStubs.push(...batchResults);
        }

        return uploadedStubs;
      };

      // Wait for everything to be uploaded to S3
      const uploadedStubs = await uploadStubsInBatches(allUploadStubs);

      // Check if the entire batch was cancelled (Cancel All)
      const currentBatch = uploadingFilesVar()[uploadBatchId];
      if (!currentBatch) {
        // Batch was cancelled, skip creating resources on server
        return;
      }

      // Get the IDs of uploads that are still active (not individually cancelled)
      const activeUploadIds = new Set(
        currentBatch.files?.map((f) => f.uploadId) || [],
      );

      const resourcesToCreate = uploadedStubs
        .filter(
          (stub) =>
            stub.resource?.storagePath && activeUploadIds.has(stub.uploadId),
        )
        .map(({ resource, name, type, mimeType, uploadPath, id }) => ({
          name,
          type,
          mimeType: resource?.mimeType ?? mimeType,
          size: resource?.size ?? 0,
          storagePath: resource!.storagePath,
          directoryPath: uploadPath ?? '',
          id: resource?.id ?? id,
        }));

      // Only create resources if there are any left after filtering cancelled ones
      if (resourcesToCreate.length > 0) {
        const createFn = createResourcesFn ?? _createResourcesOnServer;
        await createFn({
          workspaceId,
          parentId,
          resources: resourcesToCreate,
        });
      }

      // Only update state if batch still exists (not cancelled during processing)
      const finalBatch = uploadingFilesVar()[uploadBatchId];
      if (finalBatch) {
        uploadingFilesVar({
          ...uploadingFilesVar(),
          [uploadBatchId]: {
            ...finalBatch,
            isCreateResourcesDone: true,
          },
        });
      }

      // Check if we had any failed uploads and show them to the user
      const failedUploads = failedUploadsVar();
      if (failedUploads.length > 0) {
        invokePromptConfirm({
          title: t('Resource:Common.operation.upload.promptUploadFailed.title'),
          componentProps: {
            modalContent: (
              <Stack>
                <Trans
                  i18nKey="Resource:Common.operation.upload.promptUploadFailed.messageOpening"
                  transSupportBasicHtmlNodes
                />
                <List>
                  {failedUploads.map((failedUpload, index) => (
                    <List.Item key={`${index}-${failedUpload.fileName}`}>
                      <Text>{failedUpload.fileName}</Text>
                      <Text size="sm" c="dimmed">
                        {failedUpload.error}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </Stack>
            ),
            headless: true,
          },
          labels: {
            confirm: t('Common:prompts.dismiss'),
            cancel: t('Common:prompts.cancel'),
          },
          confirmProps: { color: 'blackroomOrange' },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Invokes a prompt to let user now that one or several
   * files are exceeding upload limit and thus can't be uploaded
   * @param files Oversized documents
   */
  const invokePromptExceededUploadSize = (
    files: UploadFilesData,
    onConfirm?: () => void,
  ) => {
    const maxSize = getMaxUploadSize();

    invokePromptConfirm({
      title: t('Resource:Common.operation.upload.promptUpladSizeLimit.title'),
      componentProps: {
        modalContent: (
          <Stack>
            <Trans
              i18nKey="Resource:Common.operation.upload.promptUpladSizeLimit.messageOpening"
              values={{ maxUploadSize: formatSize(maxSize) }}
              transSupportBasicHtmlNodes
            />
            <List>
              {files.map((entry, index: number) => (
                <List.Item key={`${index}-${entry.name}`}>
                  <Text>{entry.name}</Text>
                  <Text>
                    <PromiseValue
                      promise={getEntryFile(entry)?.then(
                        (file) => `(${formatSize(file?.size ?? 0)})`,
                      )}
                    />
                  </Text>
                </List.Item>
              ))}
            </List>
            <Trans
              i18nKey="Resource:Common.operation.upload.promptUpladSizeLimit.messageClosing"
              transSupportBasicHtmlNodes
            />
          </Stack>
        ),
        headless: true,
      },
      labels: {
        confirm: t('Common:prompts.dismiss'),
        cancel: t('Common:prompts.cancel'),
      },
      confirmProps: { color: 'blackroomOrange' },
      onConfirm,
    });
  };

  /**
   * Invokes a prompt to let user now that one or several
   * file have issues
   * @param files Invalid files
   */
  const invokePromptInvalidFiles = (
    data: {
      invalidFilesTypes: UploadFilesData;
      invalidFilesEmpty: UploadFilesData;
    },
    onConfirm?: () => void,
  ) => {
    invokePromptConfirm({
      title: t('Resource:Common.operation.upload.promptInvalidFiles.title'),
      zIndex: 500,
      size: 'lg',
      componentProps: {
        modalContent: (
          <Stack>
            {data.invalidFilesTypes.length > 0 && (
              <>
                <Text fw={600}>
                  <Trans
                    i18nKey="Resource:Common.operation.upload.promptInvalidFiles.messageInvalidFileTypes"
                    transSupportBasicHtmlNodes
                  />
                </Text>
                <List>
                  {data.invalidFilesTypes.map((entry, index: number) => (
                    <List.Item key={`${index}-${entry.name}`}>
                      <Text>{entry.name}</Text>
                      <Text>
                        <PromiseValue
                          promise={getEntryFile(entry)?.then(
                            (file) => `(${formatSize(file?.size ?? 0)})`,
                          )}
                        />
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </>
            )}
            {data.invalidFilesEmpty.length > 0 && (
              <>
                <Text fw={600}>
                  <Trans
                    i18nKey="Resource:Common.operation.upload.promptInvalidFiles.messageEmptyFiles"
                    transSupportBasicHtmlNodes
                  />
                </Text>
                <List>
                  {data.invalidFilesEmpty.map((entry, index: number) => (
                    <List.Item key={`${index}-${entry.name}`}>
                      <Text>{entry.name}</Text>
                      <Text>
                        <PromiseValue
                          promise={getEntryFile(entry)?.then(
                            (file) => `(${formatSize(file?.size ?? 0)})`,
                          )}
                        />
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </>
            )}
            <Trans
              i18nKey="Resource:Common.operation.upload.promptInvalidFiles.messageClosing"
              transSupportBasicHtmlNodes
            />
          </Stack>
        ),
        headless: true,
        closeImmediately: true,
      },
      labels: {
        confirm: t('Common:prompts.proceed'),
        cancel: t('Common:prompts.cancel'),
      },
      trapFocus: true,
      onConfirm,
    });
  };

  /**
   * Validates files (extension, empty, size) with user-facing prompts,
   * then uploads only the valid ones. Shared logic for all upload entry points.
   */
  const validateAndUploadFiles = async ({
    files,
    parent,
    overrideWithWorkspace,
    onReady,
    createResourcesFn,
    onValidationComplete,
  }: ValidateAndUploadFilesInput): Promise<void> => {
    if (!files.length) return;

    const maxSize = getMaxUploadSize();

    const validFiles: File[] = [];
    const invalidFilesTypes: File[] = [];
    const emptyFiles: File[] = [];
    const largeFiles: File[] = [];

    await Promise.all(
      files.map(async (file) => {
        const extension = getFileExtension(file.name);
        const validationResult = await validateUploadFileAsync(file);
        const isIgnored = ignoredExtensions.includes(extension);

        if (isIgnored) return;

        if (file.size === 0) {
          emptyFiles.push(file);
          return;
        }

        if (file.size > maxSize) {
          largeFiles.push(file);
          return;
        }

        if (!validationResult.validation) {
          invalidFilesTypes.push(file);
          return;
        }

        validFiles.push(file);
      }),
    );

    onValidationComplete?.();

    const doUpload = (filesToUpload: File[], opts?: { onReady?: () => void }) =>
      uploadSelectedResources({
        files: filesToUpload,
        parent,
        overrideWithWorkspace,
        onReady: opts?.onReady ?? onReady,
        createResourcesFn,
      });

    if (largeFiles.length > 0) {
      invokePromptExceededUploadSize(
        largeFiles,
        validFiles.length > 0
          ? () =>
              new Promise<void>((resolve) => {
                doUpload(validFiles, { onReady: resolve });
              })
          : undefined,
      );
    } else if (emptyFiles.length > 0 || invalidFilesTypes.length > 0) {
      invokePromptInvalidFiles(
        {
          invalidFilesTypes,
          invalidFilesEmpty: emptyFiles,
        },
        validFiles.length > 0 ? () => doUpload(validFiles) : undefined,
      );
    } else if (validFiles.length > 0) {
      await doUpload(validFiles);
    }
  };

  return {
    createFolder,
    uploadSelectedResources,
    validateAndUploadFiles,
    invokePromptExceededUploadSize,
    invokePromptInvalidFiles,
    generateResourceStub: _generateResourceStub,
  };
}

export default useUploader;
